import React, { Fragment } from 'react';
import { bool, func, string, any, number } from 'prop-types';
import dayjs from 'dayjs';
import get from 'lodash/get';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers';
import * as Yup from 'yup';
import Loader from 'react-loader';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import NumberFormat from 'react-number-format';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import { Controller, useForm } from 'react-hook-form';
import { chrApi } from '../../api/api';
import PatientOTP from './PatientOTP';
import { useMutation, useQuery } from 'react-query';
import { usePatientByChrId } from './hooks';

export function TextMaskCustom(props) {
  const { value, onChange, isInvalid, ...rest } = props;
  return (
    <>
      <NumberFormat customInput={Form.Control} value={value} {...rest} format="(###) ###-####" mask="_" onValueChange={(target) => onChange(target.value)} prefix={'+1'} isNumericString type={'tel'} />
      {isInvalid && <Form.Text className={'text-danger'}>Invalid value</Form.Text>}
    </>
  );
}

TextMaskCustom.propTypes = {
  value: string,
  onChange: func,
  isInvalid: any,
};

function PatientProfile(props) {
  const { showOtp = false, chrPatientId, onClose, onSave } = props;

  const schema = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    displayName: Yup.string().required('Required'),
    phone: Yup.string(),
    ssn: Yup.string(),
    dob: Yup.date().required('Required'),
    gender: Yup.string().oneOf(['Male', 'Female']).required('Required'),
    email: Yup.string().email(),
    address: Yup.object().shape({
      street: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      zip: Yup.string(),
      country: Yup.string(),
    }),
  });

  const { register, errors, handleSubmit, control, reset } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { data, isLoading } = usePatientByChrId(chrPatientId, (response = {}) => {
    reset({ ...response, dob: dayjs(response.dob).format('YYYY-MM-DD') });
  });

  //TODO: Retry if api failed

  const { mutate } = useMutation(
    (formData) => {
      const dob = dayjs(formData.dob).format('MM/DD/YYYY');
      return chrApi('put', `patient/${chrPatientId}`, { ...formData, dob });
    },
    {
      mutationKey: 'updatePatient',
      onSuccess: (data) => {
        onSave();
        toast.success('Patient Successfully Updated');
      },
    },
  );

  function onSubmit(newPatientObj) {
    mutate(newPatientObj);
    // dispatch(putPatientDetailsSuccess({ data }));
  }

  if (isLoading) {
    return <Loader loaded={false} />;
  }

  return (
    <Container fluid>
      <Row>
        <Col xs={12}>
          {showOtp && (
            <Fragment>
              <p className={'m-0'}>One-Time Password (OTP)</p>
              <PatientOTP phrPatientId={data?.phrPatientId} />
            </Fragment>
          )}
        </Col>
      </Row>
      <Row>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>First Name*</Form.Label>
            <Form.Control isInvalid={errors?.firstName} type={'text'} name={'firstName'} ref={register({ required: true })} />
            {errors.firstName && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
          </Form.Group>
        </Col>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Last Name*</Form.Label>
            <Form.Control isInvalid={errors?.lastName} type={'text'} name={'lastName'} ref={register({ required: true })} />
            {errors.lastName && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
          </Form.Group>
        </Col>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Display Name*</Form.Label>
            <Form.Control isInvalid={errors?.displayName} type={'text'} name={'displayName'} ref={register({ required: true })} />
            {errors.displayName && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
          </Form.Group>
        </Col>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Date Of Birth*</Form.Label>
            <Form.Control type={'date'} name={'dob'} ref={register({ required: true })} />
            {errors.dob && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
          </Form.Group>
        </Col>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Gender*</Form.Label>
            <Form.Control as={'select'} isInvalid={errors?.gender} name={'gender'} custom ref={register({ required: true })}>
              <option value={''}>-</option>
              <option value={'Male'}>Male</option>
              <option value={'Female'}>Female</option>
            </Form.Control>
            {errors.gender && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
          </Form.Group>
        </Col>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>SSN*</Form.Label>
            <Form.Control name={'ssn'} ref={register({ required: false })} />
            {errors.ssn && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
          </Form.Group>
        </Col>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Phone*</Form.Label>
            <Controller as={<TextMaskCustom />} defaultValue={''} isInvalid={errors?.phone} control={control} name={'phone'} rules={{ required: false }} />
            {errors.phone && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
          </Form.Group>
        </Col>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Landline</Form.Label>
            <Controller as={<TextMaskCustom />} defaultValue={''} control={control} name={'landline'} />
          </Form.Group>
        </Col>
        <Col xs={props.colLength || 12}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Email*</Form.Label>
            <Form.Control isInvalid={errors?.email} type={'email'} name={'email'} ref={register({ required: false })} />
            {errors.email && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Street</Form.Label>
            <Form.Control type={'text'} name={'address.street'} ref={register({ required: false })} />
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <Form.Group className={'mb-1'}>
            <Form.Label>City</Form.Label>
            <Form.Control type={'text'} name={'address.city'} ref={register({ required: false })} />
          </Form.Group>
        </Col>
        <Col xs={12} md={4}>
          <Form.Group className={'mb-1'}>
            <Form.Label>State</Form.Label>
            <Form.Control type={'text'} name={'address.state'} ref={register({ required: false })} />
          </Form.Group>
        </Col>
        <Col xs={12} md={4}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Country</Form.Label>
            <Form.Control type={'text'} name={'address.country'} ref={register({ required: false })} />
          </Form.Group>
        </Col>
        <Col xs={12} md={4}>
          <Form.Group className={'mb-1'}>
            <Form.Label>Zip</Form.Label>
            <Form.Control type={'text'} name={'address.zip'} ref={register({ required: false })} />
          </Form.Group>
        </Col>
      </Row>
      <Row className={'my-3'}>
        <Col>
          <Button size={'sm'} variant={'primary'} className={'mr-2'} onClick={handleSubmit(onSubmit)}>
            <FaCheck className={'mb-1 mr-1'} />
            Save
          </Button>
          <Button size={'sm'} variant={'fff'} onClick={onClose}>
            <FaTimes className={'mb-1 mr-1'} />
            Close
          </Button>
        </Col>
      </Row>
    </Container>
  );
}

PatientProfile.propTypes = {
  showOtp: bool,
  onClose: func.isRequired,
  onSave: func.isRequired,
  chrPatientId: string.isRequired,
  colLength: number,
};

export default PatientProfile;
